<template>
	<k-card class="elevation-0" style="height: 100%"
		:style="embedded_mode ? 'background-color:#999' : 'background-color:#eee'"
		:class="site_config.show_app_name_with_logo == 'true' ? 'k-framework-list-card-show-app-name' : ''"
	>
		<k-card-title
			v-if="!embedded_mode"
			class="py-0"
			:inert="framework_viewer_showing"
			:aria-hidden="framework_viewer_showing ? 'true' : 'false'"
		>
			<div class="k-toolbar-app-title k-toolbar-app-title--framework-list">{{ site_config.app_name }}</div>

			<!-- Note that this becomes visible when the window size is very narrow -->
			<h1 v-if="!frameworks_hidden" class="k-framework-list-header-line" :style="{ color: accessible_heading_color }">
				{{ site_config.framework_list_title }}
				<k-btn v-if="open_mirrors_in_satchel" icon small class="ml-1 k-help-icon-btn" style="margin-top: -4px" accessible-label="Open framework list help" @click="U.show_help('framework_index')"
					color="light-blue"><k-icon>fas fa-info-circle</k-icon></k-btn>
			</h1>

			<k-spacer></k-spacer>

			<!-- We can disable the option to toggle between Table and Tile views with `site_config.view_toggle_enabled == 'false'` in `store.js:116`. Default is Tile view -->
			<div v-if="!frameworks_hidden && !$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm && site_config.view_toggle_enabled == 'true'" class="mr-3" style="margin-top: -24px">
				<b :style="{ fontSize: '14px', color: accessible_muted_text_color }">VIEW AS:</b>
				<k-btn-toggle dense active-class="k-toggle-btn-active-class" color="primary" class="k-toggle-btn"
					v-model="framework_list_view_mode" mandatory>
					<k-btn v-if="site_config.map_mode_enabled == 'true'" small light value="map"> Map </k-btn>
					<k-btn v-if="site_config.tile_mode_enabled == 'true'" small light value="tiles">Tiles</k-btn>
					<k-btn small light value="table">Table</k-btn>
				</k-btn-toggle>
			</div>

			<k-menu
				v-model="new_framework_menu_open"
				:restore-focus-on-close="!new_framework_menu_launching_surface"
				:transition="false"
				accessible-popover
				accessible-label="New framework actions"
				content-id="new-framework-actions-panel"
				bottom
				left
			>
				<template v-slot:activator="{ on, attrs }">
					<k-btn ref="new_framework_menu_button" v-on="on" v-bind="attrs" color="primary" small class="ml-3" :style="frameworks_hidden ? 'margin:12px 0' : 'margin-top: -22px'"
						v-show="(can_create_new || user_can_create_new_for_subdomain) && !$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm">
						<k-icon small class="mr-2">fas fa-plus</k-icon> New Framework
					</k-btn>
				</template>
				<k-list id="new-framework-actions-panel" dense tabindex="-1" aria-label="New framework actions">
					<k-list-item tag="button" type="button" class="k-nav-menu-item" @click="create_new_framework"><k-list-item-icon><k-icon small>fas fa-file-circle-plus</k-icon></k-list-item-icon><k-list-item-title>Create new framework from scratch</k-list-item-title></k-list-item>
					<k-list-item v-if="can_create_new" tag="button" type="button" class="k-nav-menu-item" @click="import_framework"><k-list-item-icon><k-icon small>fas fa-file-arrow-up</k-icon></k-list-item-icon><k-list-item-title>Import framework from CASE JSON</k-list-item-title></k-list-item>
					<k-list-item v-if="can_create_new" tag="button" type="button" class="k-nav-menu-item" @click="import_framework_mirror"><k-list-item-icon><k-icon small>fas fa-clone</k-icon></k-list-item-icon><k-list-item-title>Mirror framework from another CASE server</k-list-item-title></k-list-item>
				</k-list>
			</k-menu>
		</k-card-title>

		<k-card-text
			v-if="!embedded_mode"
			:class="(!frameworks_hidden || $vuetify.breakpoint.xs) ? '' : 'mt-10'"
			:inert="framework_viewer_showing"
			:aria-hidden="framework_viewer_showing ? 'true' : 'false'"
		>
			<k-live-region :message="search_status_message" />
			<div v-if="!frameworks_hidden" class="k-framework-list-search-wrapper">
				<k-spacer />
				<!-- show category filter if we have at least two categories to show (the first category_filter_items will be help text for the menu) -->
				<!-- z-index is set here because it needs to appear above the map elements -->
				<div v-if="category_filter_items.length>2" style="width:240px; position:relative; z-index:11;" class="mr-2">
					<k-autocomplete outlined dense hide-details background-color="#fff" ref="category_autocomplete"
						v-model="category_filter_value" :items="category_filter_items" @input="category_filter_input"
						:debounce-search="0" :clearable="Boolean(category_filter_value)">
						<template v-slot:label>
							<div style="font-size:13px">Framework Category</div>
						</template>
					</k-autocomplete>
				</div>
				<div style="width:300px">
					<k-text-field dense outlined background-color="#fff" single-line hide-details v-model="search"
						@input="handle_search" :clearable="Boolean(search)">
						<template v-slot:label><div style="font-size:13px">Search framework titles/identifiers</div></template>
					</k-text-field>
				</div>
			</div>

			<div class="k-framework-list-wrapper elevation-3">
				<div class="k-framework-list-wrapper-inner">
					<!-- site_headline (don't show if a category is selected) -->
					<div v-if="(site_config.site_headline||site_config.patch_name) && !selected_category" class="k-framework-list-headline elevation-3">
						<div v-if="!framework_list_header_dismissed">

							<div v-if="site_config.patch_name" class="k-framework-list-headline-patch-name">{{site_config.patch_name}}</div>

							<div v-html="site_config.site_headline"></div>

							<!-- <div v-if="site_config.free_patch=='yes'" class="text-center mt-3" style="font-size:14px">We could show a message here if we want to do some quasi-marketing...</div> -->

							<div class="d-flex mx-2 mt-3 align-end">
								<k-btn v-if="site_config.show_subject_coverage == 'true' && !$vuetify.breakpoint.xs" small color="primary" class="k-tight-btn k-nocaps-btn" @click="show_subject_table = true">
									<k-icon small class="mr-2">fas fa-table</k-icon>Subject Coverage Table
								</k-btn>
								<k-spacer />
								<k-btn v-if="site_config.show_cross_state_comparisons == 'true' && !$vuetify.breakpoint.xs" small color="primary" class="k-tight-btn k-nocaps-btn ml-2" @click="show_subject_comps = true"><k-icon small class="mr-2">fas fa-chart-scatter</k-icon>Cross-State Comparisons</k-btn>
								<k-spacer v-if="site_config.site_headline_btns.length>0" />
								<div v-for="(params, index) in site_config.site_headline_btns" :key="index"
									:class="index ? 'ml-2' : ''">
									<k-tooltip :disabled="!params.tooltip" bottom>
										<template v-slot:activator="{ on }">
											<k-btn v-on="on" small color="primary" @click="site_headline_btn_clicked(params)"><k-icon v-if="params.icon" small class="mr-2">{{ params.icon }}</k-icon>{{ params.text}}</k-btn>
										</template>
										<div style="max-width: 400px" v-html="params.tooltip"></div>
									</k-tooltip>
								</div>
							</div>
						</div>
						<div v-if="framework_list_header_dismissed" class="d-flex align-center">
							<k-spacer />
							<div class="k-framework-list-welcome-msg" v-html="site_config.site_headline_welcome"></div>
							<k-btn small icon class="ml-3" color="light-blue" accessible-label="Show framework list welcome information" @click="framework_list_header_dismissed = false"><k-icon>fas fa-circle-info</k-icon></k-btn>
							<k-spacer />
						</div>
					</div>

					<div v-if="!frameworks_hidden">

						<!-- TILE VIEW -->
						<div v-if="framework_list_view_mode == 'tiles' || framework_mode_rendered.tiles"
							v-show="framework_list_view_mode == 'tiles'">
							<FrameworkCategory v-for="(cr, i) in category_records" :key="cr.category" 
								v-show="cr.showing"
								:search="search"
								:cr="cr"
								:case_tree_lsdoc_identifier="case_tree_lsdoc_identifier"
								@view_framework="view_framework" 
							/>

							<div v-if="(show_mirrors_checkbox || list_includes_sandbox) && !$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm" class="my-3 mr-6 pt-3 d-flex align-center justify-end" style="border-top:1px solid #999">
								<k-checkbox v-if="show_mirrors_checkbox" class="mt-0 pt-0 ml-6" hide-details 
									v-model="show_mirrors_only">
									<template v-slot:label>
										<span style="font-size:16px">Mirrors ONLY</span>
									</template>
								</k-checkbox>

								<k-checkbox v-if="list_includes_sandbox" class="mt-0 pt-0 ml-6" hide-details 
									v-model="framework_list_show_sandboxes">
									<template v-slot:label>
										<span style="font-size:16px">Show sandboxes</span>
									</template>
								</k-checkbox>

								<!-- PW 2/2026: remove this -->
								<!-- <v-checkbox v-if="show_no_limiters_checkbox" class="mt-0 pt-0 ml-6" hide-details 
									v-model="framework_list_force_include_all_frameworks">
									<template v-slot:label>
										<span style="font-size:16px">Don’t limit by category/subject</span>
									</template>
								</v-checkbox> -->
							</div>
						</div>

						<!-- MAP VIEW -->
						<div v-if="framework_list_view_mode == 'map' || framework_mode_rendered.map" v-show="framework_list_view_mode == 'map'">
							<FrameworkMap ref="framework_map" 
								:category_records="category_records"
								@view_framework="view_framework" 
							/>
						</div>

						<!-- TABLE VIEW -->
						<div v-if="framework_list_view_mode == 'table' || framework_mode_rendered.table" v-show="framework_list_view_mode == 'table'" style="position:relative" class="pt-2">
							<div v-if="selected_category && selected_category.description" class="k-framework-selected-category-description elevation-3">
								<div v-html="selected_category.description"></div>
							</div>
							<div v-if="selected_frameworks.length > 0" style="position: relative; z-index: 1; left: 50px; width: 200px; background-color: #fff">
								<k-menu v-model="selected_framework_actions_menu_open" accessible-popover accessible-label="Actions for selected frameworks" content-id="framework-selected-actions-panel" :transition="false" offset-x top right>
									<template v-slot:activator="{ on, attrs }">
										<k-btn v-on="on" v-bind="attrs" small color="#666" dark accessible-label="Open actions for selected frameworks"><k-icon small class="mr-2">fas fa-arrow-right</k-icon> With selected…</k-btn>
									</template>
									<k-list id="framework-selected-actions-panel" min-width="250" dense tabindex="-1" aria-label="Actions for selected frameworks">
										<k-list-item tag="button" type="button" class="k-nav-menu-item" @click="copy_framework_data">
											<k-list-item-icon><k-icon small>fas fa-clipboard</k-icon></k-list-item-icon>
											<k-list-item-title>Copy framework data to clipboard…</k-list-item-title>
										</k-list-item>
										<k-list-item v-show="should_have_crosswalk_menu_item" tag="button" type="button" class="k-nav-menu-item" @click="open_selected_crosswalk">
											<k-list-item-icon><k-icon small>fas fa-shuffle</k-icon></k-list-item-icon>
											<k-list-item-title>Create/edit crosswalk…</k-list-item-title>
										</k-list-item>
										<!-- <k-list-item @click="export_frameworks"><k-list-item-icon><k-icon small>fas fa-file-download</k-icon></k-list-item-icon><k-list-item-title>Download framework(s)…</k-list-item-title></k-list-item> -->
										<!-- <k-list-item @click="rename_frameworks"><k-list-item-icon><k-icon small>fas fa-heading</k-icon></k-list-item-icon><k-list-item-title>Rename framework(s)…</k-list-item-title></k-list-item> -->
										<k-list-item v-if="can_bulk_edit" tag="button" type="button" class="k-nav-menu-item" @click="change_framework_status">
											<k-list-item-icon><k-icon small>fas fa-flag</k-icon></k-list-item-icon>
											<k-list-item-title>Update adoption status for frameworks…</k-list-item-title>
										</k-list-item>
										<k-list-item v-if="can_bulk_edit" tag="button" type="button" class="k-nav-menu-item" @click="delete_frameworks">
											<k-list-item-icon><k-icon small>fas fa-trash-alt</k-icon></k-list-item-icon>
											<k-list-item-title>Delete frameworks…</k-list-item-title>
										</k-list-item>
									</k-list>
								</k-menu>
							</div>

							<k-data-table dense class="k-framework-list-table" show-select 
								v-model="selected_frameworks"
								item-key="identifier" 
								:headers="table_headers" 
								:items="table_filtered_frameworks"
								:items-per-page="50" 
								:footer-props="footer_options" 
								:must-sort="true"
								:sort-by.sync="table_sort_by" 
								:sort-desc.sync="table_sort_desc"
							>
								<template v-slot:data-table-select="{ props, on }">
									<k-checkbox
										data-k-selection-header-control="true"
										class="mt-0 pt-0"
										hide-details
										:value="props.value"
										:indeterminate="props.indeterminate"
										:accessible-label="selectAllFrameworksLabel"
										@click.stop
										@input="on.input"
									>
										<template v-slot:label>
											<span class="k-sr-only">{{ selectAllFrameworksLabel }}</span>
										</template>
									</k-checkbox>
								</template>
								<template v-slot:item.data-table-select="{ item, isSelected, select }">
									<k-checkbox
										class="mt-0 pt-0"
										hide-details
										:value="isSelected"
										:accessible-label="selectFrameworkRowLabel(item)"
										@click.stop
										@input="select($event)"
									>
										<template v-slot:label>
											<span class="k-sr-only">{{ selectFrameworkRowLabel(item) }}</span>
										</template>
									</k-checkbox>
								</template>
								<template v-slot:item.title="{ item }">
									<div class="d-flex align-center">
										<div
											aria-hidden="true"
											class="k-framework-list-table-icon mr-1 my-1"
											:class="framework_color(item)"
											:style="framework_color_style(item)"
										><k-icon small>{{framework_icon(item)}}</k-icon></div>
										<div class="ml-2">
											<a :href="frameworkPath(item.identifier)" v-html="item.title" @click.prevent="view_framework(item.identifier, null, $event)"></a>
										</div>
										<k-tooltip bottom>
											<template v-slot:activator="{ on }">
												<div v-on="on" v-if="item.is_sandbox" class="ml-3" style="white-space: nowrap">
													<k-icon small class="mr-1" color="primary">fas fa-umbrella-beach</k-icon>
												</div>
											</template>
											<div class="text-center">This is a “sandbox” copy of the original framework.</div>
										</k-tooltip>
										<k-tooltip bottom>
											<template v-slot:activator="{ on }">
												<div v-on="on" v-if="item.is_derivative && user_can_edit[item.identifier]" class="ml-3" style="white-space: nowrap">
													<k-icon small class="mr-1" color="primary">fas fa-arrow-up-from-bracket</k-icon>
												</div>
											</template>
											<div class="text-center">This is a “derivative” copy of another framework.</div>
										</k-tooltip>
										<k-tooltip bottom>
											<template v-slot:activator="{on}">
												<div v-on="on" v-if="item.is_private" class="ml-3" style="white-space: nowrap">
													<k-icon small class="mr-1" color="primary">fas fa-user-secret</k-icon>
												</div>
											</template>
											<div class="text-center">This framework is PRIVATE:<br>it will only be visible to explicitly-authorized users.</div>
										</k-tooltip>
									</div>
								</template>
								<template v-slot:item.adoptionStatus="{ item }">
									<div class="k-case-tree-item-adoption-status" :class="adoption_status_css_class(item)">
										<nobr>
											{{ item.adoption_status_display_value }}
											{{ item.is_private ? '(Private)' : '' }}
										</nobr>
									</div>
								</template>
								<template v-slot:item.category="{ item }">
									<button type="button" class="k-framework-list-table-link-btn" @click="category_filter_value = item.full_category_text">
										{{ item.category }}</button>
								</template>
								<template v-slot:item.lastChangeDateTime="{ item }">
									<nobr>
										{{ formatted_date(item.lastChangeDateTime) }}
										<k-tooltip bottom>
											<template v-slot:activator="{ on }">
												<k-btn v-on="on" v-show="item.is_mirror == 'yes' && user_can_edit[item.identifier]" 
													class="ml-2" x-small fab outlined 
													:color="item.mirror_ok ? 'cyan darken-3' : 'pink darken-3'"
													:accessible-label="mirrorInfoLabel(item)"
													@click="mirror_to_show = item.identifier"
												>
													<k-icon>fas {{ item.mirror_ok ? 'fa-clone' : 'fa-triangle-exclamation' }}</k-icon>
												</k-btn></template>
											<div class="text-center">This framework is “mirrored”.<br />Click for info</div>
										</k-tooltip>
									</nobr>
								</template>
								<template v-slot:item.identifier="{ item }">
									<nobr><span class="k-framework-list-identifier-text" v-html="item.identifier"></span>
										<CopyBtn :size="'small'" color="#999" :val="item.identifier" :label="`Copy framework identifier for ${plainFrameworkTitle(item)}`" />
									</nobr>
								</template>
								<template v-slot:item.issuing_agency="{ item }">
									<div style="font-size: 12px; line-height:15px;" v-html="item.issuing_agency"></div>
								</template>
								<template v-slot:item.case_publisher="{ item }">
									<nobr><span style="font-size: 12px" v-html="item.case_publisher"></span></nobr>
								</template>
								<template v-slot:item.n_crosswalks="{ item }">
									<k-btn v-if="item.n_crosswalks > 0" 
										small outlined color="primary"
										@click.stop="show_crosswalks_for_identifier = item.identifier"
										:disabled="!has_crosswalk_editor_access"
									>
										<span style="font-size: 18px">{{ item.n_crosswalks }}</span>
										<k-icon small class="ml-1">fas fa-shuffle</k-icon>
									</k-btn>
								</template>
								<template v-slot:item.bp_score_sort="{ item }">
									<button
										v-if="item.bp_display"
										type="button"
										class="k-framework-list-bp-score"
										style="margin-top:-2px"
										:aria-label="`Open best practices review for ${plainFrameworkTitle(item)}`"
										v-html="item.bp_display"
										@click="show_best_practices(item.identifier)"
									></button>
									<div v-else class="text-center">
										<k-btn x-small outlined color="secondary"
											@click="show_best_practices(item.identifier)">Check</k-btn>
									</div>
								</template>
							</k-data-table>
							<div v-if="!$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm" class="my-3 d-flex align-center justify-center">
								<span>Show in table:</span>

								<k-checkbox class="mt-0 pt-0 ml-6" hide-details
									v-model="show_identifiers_in_framework_table">
									<template v-slot:label>
										<span style="font-size:14px;white-space:nowrap;">Framework identifiers
											(GUIDs)</span>
									</template>
								</k-checkbox>

								<k-checkbox v-if="bp_review_summaries_available" class="mt-0 pt-0 ml-6" hide-details
									v-model="bp_results_showing_in_framework_table">
									<template v-slot:label>
										<span style="font-size:14px;white-space:nowrap;">Best Practices Evaluations</span>
									</template>
								</k-checkbox>

								<k-checkbox v-if="show_crosswalks_checkbox && framework_list_view_mode == 'table'" class="mt-0 pt-0 ml-6" hide-details 
									v-model="show_crosswalks">
									<template v-slot:label>
										<span style="font-size:14px">Crosswalks</span>
									</template>
								</k-checkbox>

								<k-checkbox v-if="show_mirrors_checkbox" class="mt-0 pt-0 ml-6" hide-details 
									v-model="show_mirrors_only">
									<template v-slot:label>
										<span style="font-size:14px">Mirrors ONLY</span>
									</template>
								</k-checkbox>

								<k-checkbox v-if="list_includes_sandbox" class="mt-0 pt-0 ml-6" hide-details 
									v-model="framework_list_show_sandboxes">
									<template v-slot:label>
										<span style="font-size:14px">Sandboxes</span>
									</template>
								</k-checkbox>

								<!-- PW 2/2026: remove this -->
								<!-- <v-checkbox v-if="show_no_limiters_checkbox" class="mt-0 pt-0 ml-6" hide-details 
									v-model="framework_list_force_include_all_frameworks">
									<template v-slot:label>
										<span style="font-size:14px">Don’t limit by category/subject</span>
									</template>
								</v-checkbox> -->
							</div>


							<!-- <div class="my-3 d-flex align-center"><v-spacer/> -->
							<!-- <div v-if="n_creator_publisher_mismatches>0" class="ml-3" style="font-size:14px; line-height:17px; max-width:540px;"><b>Note:</b> Frameworks whose status value is shown in <b class="k-case-tree-item-adoption-status-unofficial white--text px-1" style="border-radius:4px">BLUE</b> have been published in CASE format by an organization other than the issuing agency for the framework.</div> -->
							<!-- <v-spacer/></div> -->
						</div>

					</div>
				</div>
			</div>

			<div v-if="cureum_standards_tabbed" style="position: absolute; left: 20px; bottom: 8px; font-size: 14px"><b>{{ site_config.app_name }} v{{ $store.state.app_version }}</b><span v-if="signed_in" class="ml-6">Signed in as: {{ user_info.email }}</span></div>
		</k-card-text>

		<CASEFrameworkViewer
			v-if="initialized && !case_tree_lsdoc_identifier_is_shortcut && case_tree_lsdoc_identifier && open_mirrors_in_satchel"
			ref="framework_viewer"
			:lsdoc_identifier="case_tree_lsdoc_identifier"
			:starting_lsitem_identifier="starting_lsitem_identifier"
			:starting_tree_key="starting_tree_key"
			:minimized_param="minimized_param"
			@hide_tree="hide_case_tree"
			@set_mode="set_mode"
			@set_starting_lsitem_identifier="set_starting_lsitem_identifier"
		/>
		<CrosswalkEditor
			v-if="initialized && crosswalk_lsdoc_identifiers"
			:crosswalk_lsdoc_identifiers="crosswalk_lsdoc_identifiers"
		/>
		<DocumentEditor
			v-if="new_framework"
			:framework_record="new_framework"
			:restore-focus-element="new_framework_focus_origin"
			@dialog_cancel="cancel_new_framework"
			@view_framework="view_framework"
		/>
		<ImportMirror
			v-if="show_import_mirror_dialog"
			:restore-focus-element="new_framework_focus_origin"
			@view_framework="view_framework"
			@dialog_cancel="show_import_mirror_dialog = false"
		/>
		<MirrorInfo
			v-if="mirror_to_show"
			:lsdoc_identifier="mirror_to_show"
			@mirror_info_cancel="mirror_to_show = null"
		/>
		<FrameworkSubjectTable
			v-if="show_subject_table"
			:category_records="category_records"
			@dialog_cancel="show_subject_table = false"
		/>
		<SubjectComps
			v-if="show_subject_comps"
			@dialog_cancel="show_subject_comps = false"
		/>
		<TrackChangesLegend
			v-show="tracking_changes"
			ref="track_changes_legend"
		/>
		<CrosswalkListForIdentifier
			v-if="show_crosswalks_for_identifier"
			:framework_identifier="show_crosswalks_for_identifier"
			@open_crosswalk="open_crosswalk"
			@dialog_cancel="show_crosswalks_for_identifier = null"
		/>
		<BestPracticesReport 
			v-if="best_practices_fr"
			:framework_record="best_practices_fr"
			@dialog_cancel="best_practices_fr=null" 
		/>
	</k-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import FrameworkCategory from './FrameworkCategory'
import FrameworkMap from './FrameworkMap'
import CASEFrameworkViewer from '../CASEFrameworkViewer/CASEFrameworkViewer'
import CrosswalkEditor from '../crosswalks/CrosswalkEditor'
import DocumentEditor from '../edit/DocumentEditor'
import ImportMirror from '../edit/ImportMirror'
import MirrorInfo from '../CASEFrameworkViewer/MirrorInfo'
import FrameworkSubjectTable from './FrameworkSubjectTable'
import SubjectComps from './SubjectComps'
import CopyBtn from '../utilities/CopyBtn'
import TrackChangesLegend from '../CASEFrameworkViewer/TrackChangesLegend'
import CrosswalkListForIdentifier from '../crosswalks/CrosswalkListForIdentifier'
import BestPracticesReport from '../admin/BestPracticesReport'
import goTo from 'vuetify/lib/services/goto'

export default {
	components: {
		FrameworkCategory,
		CASEFrameworkViewer,
		CrosswalkEditor,
		DocumentEditor,
		ImportMirror,
		MirrorInfo,
		FrameworkMap,
		FrameworkSubjectTable,
		SubjectComps,
		CopyBtn,
		TrackChangesLegend,
		CrosswalkListForIdentifier,
		BestPracticesReport
	},
	props: {
		case_tree_lsdoc_identifier: { type: String, required: false, default() { return '' }},
		starting_lsitem_identifier: { type: String, required: false, default() { return '' }},
		starting_tree_key: { type: String, required: false, default() { '' }},
		query: { type: Object, required: false, default() { return {} }},
		crosswalk_lsdoc_identifiers: { required: false, default() { return null } },
		// crosswalk_lsdoc_identifier_2: { type: String, required: false, default() { return '' }},
	},
	data() {
		return {
			initialized: false,
			search: null,
			category_records: [],
			new_framework: null,
			selected_frameworks: [],
			selected_framework_actions_menu_open: false,
			new_framework_menu_open: false,
			new_framework_menu_launching_surface: false,
			import_framework_dialog_pending: false,
			new_framework_focus_origin: null,
			show_import_mirror_dialog: false,
			mirror_to_show: null,
			show_subject_table: false,
			show_subject_comps: false,
			n_creator_publisher_mismatches: 0,
			show_crosswalks_for_identifier: null,
			footer_options: {
				itemsPerPageOptions: [20, 50, 100, { text: 'All', value: -1 }]
			},
			best_practices_fr: null,
			category_filter_value: null,
			search_status_message: '',
			framework_mode_rendered: {
				map: false,
				table: false,
				tiles: false,
			},
		}
	},
	computed: {
		...mapState(['framework_records', 'user_info', 'site_config', 'embedded_mode', 'cureum_standards_tabbed']),
		...mapGetters(['signed_in', 'crosswalk_framework_hash', 'open_mirrors_in_satchel', 'viewing_subdomain', 'home_category', 'instance_code', 'user_can_create_new_for_subdomain', 'frameworks_hidden', 'bp_review_full_reports_available', 'bp_review_summaries_available']),
		framework_viewer_showing() {
			return this.initialized && !this.case_tree_lsdoc_identifier_is_shortcut && !!this.case_tree_lsdoc_identifier && this.open_mirrors_in_satchel
		},
		has_crosswalk_editor_access() {
			const is_super_user = vapp.is_granted('super')
			return is_super_user || this.$store.state.site_config.enable_crosswalk_editor_access == 'true'
		},
		should_have_crosswalk_menu_item() {
			const has_two_frameworks_selected = this.selected_frameworks.length == 2
			return has_two_frameworks_selected && this.has_crosswalk_editor_access && this.signed_in
		},
		show_mirrors_checkbox() {
			// show the checkbox for limiting to mirrors only if we're showing the table view and the user is a system admin
			if (this.framework_list_view_mode == 'table' && vapp.is_granted('super')) {
				// and we have at least one mirror
				return this.framework_records.findIndex(x => x.ss_framework_data.is_mirror == 'yes') > -1
			}
			return false
		},
		show_crosswalks_checkbox() {
			// show the checkbox for showing crosswalks if we're showing the table view and the user is a system admin (currently; we'll expand this later)
			if (this.framework_list_view_mode == 'table' && vapp.is_granted('super')) {
				return true
				// further limit to pepper for now
				// if (!this.user_info.email.includes('pepper')) return false
				// and we have at least one crosswalk
				// return this.framework_records.findIndex(x=>x.ss_framework_data.is_mirror=='yes') > -1
			}
			return false
		},
		show_no_limiters_checkbox() {
			if (!vapp.is_granted('super')) return false
			if (!empty(this.site_config.included_categories)) return true
			if (!empty(this.site_config.included_subjects)) return true
			return false
		},
		accessible_heading_color() {
			return U.get_accessible_text_color('#ffffff', this.site_config.heading_color)
		},
		accessible_muted_text_color() {
			return U.get_accessible_text_color('#ffffff', '#666666')
		},
		selectAllFrameworksLabel() {
			return 'Select all frameworks'
		},
		// PW 2/2026: remove this
		// framework_list_force_include_all_frameworks: {
		// 	get() {
		// 		return this.$store.state.lst.framework_list_force_include_all_frameworks
		// 	},
		// 	set(val) {
		// 		this.$store.commit('lst_set', ['framework_list_force_include_all_frameworks', val])
		// 	}
		// },
		framework_list_header_dismissed: {
			get() {
				return this.$store.state.lst.framework_list_header_dismissed
			},
			set(val) {
				this.$store.commit('lst_set', ['framework_list_header_dismissed', val])
			}
		},
		table_sort_by: {
			get() {
				return this.$store.state.lst.framework_list_table_sort_by
			},
			set(val) {
				this.$store.commit('lst_set', ['framework_list_table_sort_by', val])
			}
		},
		table_sort_desc: {
			get() {
				return this.$store.state.lst.framework_list_table_sort_desc
			},
			set(val) {
				this.$store.commit('lst_set', ['framework_list_table_sort_desc', val])
			}
		},
		framework_list_show_sandboxes: {
			get() {
				return this.$store.state.lst.framework_list_show_sandboxes
			},
			set(val) {
				this.$store.commit('lst_set', ['framework_list_show_sandboxes', val])
			}
		},
		show_mirrors_only: {
			get() {
				return this.show_mirrors_checkbox && this.$store.state.lst.framework_list_show_mirrors_only
			},
			set(val) {
				this.$store.commit('lst_set', ['framework_list_show_mirrors_only', val])
			}
		},
		show_crosswalks: {
			get() {
				return this.show_crosswalks_checkbox && this.$store.state.lst.framework_list_show_crosswalks
			},
			set(val) {
				this.$store.commit('lst_set', ['framework_list_show_crosswalks', val])
			}
		},
		can_bulk_edit() {
			// people with super-user access can bulk edit
			return vapp.is_granted('super')
		},
		can_create_new() {
			if (!this.signed_in) return false
			return vapp.is_granted('create_new_framework')
		},
		minimized_param() {
			return this.query.minimized !== undefined
		},
		framework_list_view_mode: {
			get() {
				// this.$alert(`this.$vuetify.breakpoint.xs: ${this.$vuetify.breakpoint.xs} / this.$vuetify.breakpoint.sm: ${this.$vuetify.breakpoint.sm}`)
				// true/false - portrait -- false / true - landscape
				// first get value we're setting to
				let m

				// on small screens, always use tile view -- we don't have room for a table or map
				if (this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm) {
					m = 'tiles'

				// otherwise...
				} else {
					// otherwise look at lst.framework_list_view_mode if the user has set it
					if (!empty(this.$store.state.lst.framework_list_view_mode)) {
						m = this.$store.state.lst.framework_list_view_mode

						// make sure this mode is available
						if (m == 'map' && this.site_config.map_mode_enabled == 'false') m = ''
						if (m == 'tiles' && this.site_config.tile_mode_enabled == 'false') m = ''
					}

					// if m is still empty, decide on a default...
					if (this.site_config.view_toggle_enabled == 'false' || empty(m)) {
						// use map if map_mode_enabled == 'true'
						if (this.site_config.map_mode_enabled == 'true') m = 'map'
						// or tiles unless tile_mode_enabled is 'false'
						else if (this.site_config.tile_mode_enabled != 'false') m = 'tiles'
						// fallback: 'table'
						else m = 'table'
					}
				}

				// before returning, set framework_mode_rendered[m] to true; we render each mode's component if both framework_list_view_mode == m and framework_mode_rendered[m] is true
				this.framework_mode_rendered[m] = true

				return m
			},
			set(val) {
				this.$store.commit('lst_set', ['framework_list_view_mode', val])
			}
		},
		show_identifiers_in_framework_table: {
			get() {
				return this.$store.state.lst.show_identifiers_in_framework_table
			},
			set(val) {
				this.$store.commit('lst_set', ['show_identifiers_in_framework_table', val])
			}
		},
		bp_results_showing_in_framework_table: {
			get() { return this.$store.state.lst.bp_results_showing_in_framework_table },
			set(val) { this.$store.commit('lst_set', ['bp_results_showing_in_framework_table', val]) }
		},
		table_headers() {
			if (this.open_mirrors_in_satchel) {
				// "Normal" satchel
				let arr = [
					{ text: 'Category', value: 'category', sortable: true, align: 'left' },
					{ text: 'Framework Title', value: 'title', sortable: true, align: 'left' },
					{ text: 'Subject', value: 'subject', sortable: true, align: 'left' },
					{ text: 'Status', value: 'adoptionStatus', sortable: true, align: 'center' },
					{
						text: this.show_mirrors_only ? 'Last Mirror Check' : 'Last Changed',
						value: 'lastChangeDateTime',
						sortable: true,
						align: 'left'
					}
				]
				if (this.show_crosswalks) {
					// arr.push({ text: ' X-To ', value: 'n_crosswalks_to', sortable: true, align: 'center' })
					// arr.push({ text: 'X-From', value: 'n_crosswalks_from', sortable: true, align: 'center' })
					arr.push({ text: 'Crosswalks', value: 'n_crosswalks', sortable: true, align: 'center' })
				}
				if (this.show_identifiers_in_framework_table) {
					arr.push({ text: 'Identifier', value: 'identifier', sortable: true, align: 'center' })
				}
				if (this.bp_results_showing_in_framework_table && this.bp_review_summaries_available) {
					arr.push({ text: '# Items', value: 'item_count', sortable: true, align: 'center' })
					// arr.push({ text: '# Assoc', value: 'association_count', sortable: true, align: 'center' })
					arr.push({ text: 'Best Practices', value: 'bp_score_sort', sortable: true, align: 'left' })
				}
				return arr

			// "registry" version
			} else {
				let arr = [
					{ text: 'Issuing Agency', value: 'issuing_agency', sortable: true, align: 'left' },
					{ text: 'CASE Publisher (if different)', value: 'case_publisher', sortable: true, align: 'left' },
					{ text: 'Framework Title', value: 'title', sortable: true, align: 'left' },
					{ text: 'Restricted?', value: 'restricted', sortable: true, align: 'center' },
					// { text: 'Status', value: 'adoptionStatus', sortable: true, align: 'center' },
					{ text: 'Last Changed', value: 'lastChangeDateTime', sortable: true, align: 'left' },
				]
				if (this.show_identifiers_in_framework_table) {
					arr.push({ text: 'Identifier', value: 'identifier', sortable: true, align: 'center' })
				}
				return arr
			}
		},
		table_filtered_frameworks() {
			let n_creator_publisher_mismatches = 0 // count these as we go
			let arr = []
			for (let cr of this.category_records) {
				for (let fr of cr.framework_records) {
					if (cr.doc_showing[fr.lsdoc_identifier]) {
						let full_category_text = fr.ss_framework_data.category
						let category_data = U.parse_framework_category(full_category_text)

						// guess at subject; make non-breaking
						let subject = U.framework_subject_guess(fr.json.CFDocument)['label'].replace(/ /g, ' ')

						// compute bp_display value
						let bp_display = ''
						let bp_score = fr.ss_framework_data.bp_score
						let bp_score_sort = bp_score * 10000000
						let bp_issue_count = fr.ss_framework_data.bp_issue_count
						let bp_issue_type_count = fr.ss_framework_data.bp_issue_type_count
						if (bp_issue_count > -1) {
							bp_score_sort += bp_issue_type_count*100000
							bp_score_sort += bp_issue_count*1
							bp_display = `<span class="k-framework-list-bp-score">`
							bp_display += this.bp_severity_indicator(bp_score)
							if (bp_issue_count == 0) bp_display += `<span class="ml-1">no issues</span>`
							else bp_display += `<b class="ml-1">${bp_issue_type_count} / ${bp_issue_count} ${U.ps('issue', bp_issue_count)}</b>`
							bp_display += `</span>`
						}

						// let case_publisher = (cr.cglt_published ? 'Common Good Learning Tools' : cr.agency_name)
						let case_publisher = (cr.cglt_published ? 'Common Good Learning Tools' : '')
						let restricted = (cr.cglt_published ? 'Yes' : '')

						arr.push({
							full_category_text: full_category_text,
							category: category_data.title,
							map_label: category_data.map_label,
							identifier: fr.json.CFDocument.identifier,
							title: fr.json.CFDocument.title,
							subject: subject,
							creator: fr.json.CFDocument.creator,
							publisher: fr.json.CFDocument.publisher,
							adoptionStatus: fr.json.CFDocument.adoptionStatus,
							adoption_status_display_value: CFDocument.adoption_status_display_value(fr.json.CFDocument.adoptionStatus),
							frameworkType: fr.json.CFDocument.frameworkType,
							is_mirror: fr.ss_framework_data.is_mirror,
							is_private: fr.ss_framework_data.is_private,
							mirror_ok: fr.ss_framework_data.last_mirror_sync_status?.includes('OK'),
							bp_display: bp_display,
							bp_score: bp_score,
							bp_score_sort: bp_score_sort,
							item_count: fr.ss_framework_data.item_count,
							association_count: fr.ss_framework_data.association_count,

							issuing_agency: cr.agency_name,
							case_publisher: case_publisher,
							restricted: restricted,

							// for lastChangeDateTime, use mirror_sync_date if show_mirrors_only is true
							lastChangeDateTime: this.show_mirrors_only
								? date.format(U.convert_to_local_date(fr.ss_framework_data.last_mirror_sync_date), 'YYYY-MM-DD HH:mm')
								: fr.json.CFDocument.lastChangeDateTime,

							// number of crosswalks
							n_crosswalks: this.crosswalk_framework_hash[fr.json.CFDocument.identifier]?.length ?? 0,

							is_sandbox: !empty(fr.ss_framework_data.sandboxOfIdentifier),
							is_derivative: !empty(fr.json.CFDocument.sourceFrameworkIdentifier) || !empty(fr.json.CFDocument.extensions?.sourceFrameworkIdentifier)
						})

						if (!empty(fr.json.CFDocument.publisher) && fr.json.CFDocument.creator != fr.json.CFDocument.publisher)
							++n_creator_publisher_mismatches
					}
				}
			}
			this.n_creator_publisher_mismatches = n_creator_publisher_mismatches
			// console.log(`Total frameworks: ${arr.length} / creator-publisher mismatches: ${n_creator_publisher_mismatches} (${(n_creator_publisher_mismatches/arr.length*100).toFixed(1)}%)`)
			return arr
		},
		list_includes_sandbox() {
			for (let fr of this.$store.getters.filtered_framework_records) {
				if (!empty(fr.ss_framework_data.sandboxOfIdentifier)) return true
			}
		},
		// not currently used
		list_includes_crosswalk() {
			for (let fr of this.framework_records) {
				if (!vapp.is_granted('view_framework', fr.lsdoc_identifier)) continue
				if (fr.json.CFDocument.frameworkType == 'crosswalk') return true
			}
		},
		case_tree_lsdoc_identifier_is_identifier() {
			return (this.case_tree_lsdoc_identifier.search(/^........-....-....-....-............/) == 0)
		},
		case_tree_lsdoc_identifier_is_shortcut() {
			if (!this.case_tree_lsdoc_identifier) return false
			if (this.case_tree_lsdoc_identifier_is_identifier) return false
			return true
		},
		user_can_edit() {
			if (!this.signed_in) return {}
			let o = {}
			for (let fr of this.framework_records) {
				o[fr.lsdoc_identifier] = vapp.is_granted('edit_framework', fr.lsdoc_identifier)
			}
			return o
		},
		tracking_changes() {
			return this.case_tree_lsdoc_identifier && vapp.case_tree_component && vapp.case_tree_component.tracking_changes
		},

		// items for the "Framework category" v-autocomplete
		category_filter_items() {
			let arr = []
			arr.push({value: 'xxx', text: '--- SHOW ALL CATEGORIES ---'})
			for (let cr of this.category_records) {
				arr.push({
					value: cr.category,
					text: cr.title
				})
			}
			return arr
		},

		// when category_filter_value changes, get the selected category and do some side effects
		selected_category() {
			// empty string for category_filter_value corresponds to no selected_category -- null value
			let cr
			if (empty(this.category_filter_value) || this.category_filter_value == 'xxx') {
				cr = null

				// this forces the v-autocomplete to be cleared properly if "SHOW ALL" is chosen
				setTimeout(x=>{
					this.category_filter_value = null
				}, 0)

				// close the expanded square if we're in map view
				if (this.framework_list_view_mode == 'map' && this.$refs.framework_map) {
					this.$refs.framework_map.close_expanded_square()
				}

			} else {
				cr = this.category_records.find(x=>x.category == this.category_filter_value)

				// open the expanded square if we're in map view
				if (this.framework_list_view_mode == 'map' && this.$refs.framework_map) {
					// but not if we're showing a framework
					if (!this.case_tree_lsdoc_identifier_is_identifier) {
						this.$refs.framework_map.square_clicked(null, this.category_filter_value)
					}
				}
			}

			// update the url if necessary
			if (!this.case_tree_lsdoc_identifier_is_identifier) {
				if (cr && cr.map_label) {
					// console.warn('selected_category computed: changing to ' + `/${cr.map_label}`)
					U.push_state_if_url_changed(null, '', `/${cr.map_label}`)
				} else {
					// console.warn('selected_category computed; changing to empty ')
					U.push_state_if_url_changed(null, '', `/`)
				}
			}

			// blur the autocomplete menu when changed
			if (this.$refs.category_autocomplete) {
				setTimeout(x=>{
					this.$refs.category_autocomplete.blur()
					this.$refs.category_autocomplete.isMenuActive = false
				}, 0)
			}

			this.handle_search()
			return cr
		},
	},
	watch: {
		framework_records: {
			immediate: true,
			handler(val) {
				this.create_category_records()
				this.initialized = true
				this.redirect_shortcut()
			}
		},
		// PW 2/2026: remove this
		// framework_list_force_include_all_frameworks() {
		// 	this.create_category_records()
		// },
		framework_list_show_sandboxes() {
			this.create_category_records()
		},
		show_mirrors_only() {
			this.create_category_records()
		},
		signed_in() {
			// when the user signs in, we have to re-create the category records and re-filter based on the search terms
			this.create_category_records()
			this.handle_search()
		},
		case_tree_lsdoc_identifier() {
			// by calling handle_search whenever this changes, we re-filter based on the search terms whenever the user comes back to this page after viewing a framework
			this.handle_search()

			this.redirect_shortcut()
		},
	},
	created() {
		// make sure frameworks_or_resources_toggle is set to 'frameworks'
		this.$store.commit('lst_set', ['frameworks_or_resources_toggle', 'frameworks'])

		vapp.framework_list_component = this
		// to check to see if a given framework_identifier is showing:
		// vapp.framework_list_component.case_tree_lsdoc_identifier == framework_identifier
	},
	mounted() {
	},
	beforeRouteLeave(to, from, next) {
		// this doesn't work properly, and isn't needed, in embedded mode
		if (this.embedded_mode) {
			next()
		}
		/*	
		*	These routes are routes that use the Framework List as the root component, but
		*   but are not actually the framework list itself, and instead are showing a framework.
		*/
		const framework_routes = ['framework', 'framework_item_with_key', 'framework_item']

		const is_routing_from_framework_route = framework_routes.includes(from.name)
		const is_routing_to_non_framework_route = !framework_routes.includes(to.name)
		const is_navigating_away_from_framework = is_routing_from_framework_route && is_routing_to_non_framework_route
		
		if (is_navigating_away_from_framework) {
			if (this.$refs.framework_viewer && this.$refs.framework_viewer.check_for_stamp_reminder_dialog) {
				this.$refs.framework_viewer.check_for_stamp_reminder_dialog()
					.then(() => {
						next()
					})
			}
		} else {
			next()
		}
	},
	methods: {
		setSearchStatusMessage(message) {
			this.search_status_message = ''
			this.$nextTick(() => {
				this.search_status_message = message
			})
		},
		visibleFrameworkCount() {
			return this.category_records.reduce((count, categoryRecord) => {
				return count + categoryRecord.framework_records.filter(record => categoryRecord.doc_showing[record.lsdoc_identifier]).length
			}, 0)
		},
		updateSearchStatusMessage() {
			if (this.frameworks_hidden) return
			const visibleCount = this.visibleFrameworkCount()
			const categoryPrefix = this.category_filter_value ? `${this.category_filter_value}. ` : ''
			const searchPrefix = this.search ? `Results for ${this.search}. ` : ''
			this.setSearchStatusMessage(`${categoryPrefix}${searchPrefix}${visibleCount} ${U.ps('framework', visibleCount)} shown.`)
		},
		plainText(value) {
			return $('<div>').html(value || '').text().trim()
		},
		plainFrameworkTitle(item) {
			return this.plainText(item?.title) || 'framework'
		},
		frameworkPath(identifier) {
			return `/${identifier}`
		},
		mirrorInfoLabel(item) {
			return `Show mirror status for ${this.plainFrameworkTitle(item)}`
		},
		selectFrameworkRowLabel(item) {
			return `Select framework ${this.plainFrameworkTitle(item)}`
		},

		// if the user used a "url shortcut" (e.g. "math") in the url, look up the corresponding framework and redirect to it
		redirect_shortcut() {
			// we also handle here the case where initialize_app sent back a start_string_identifiers value
			if (this.$store.state.start_string_identifiers) {
				this.view_framework(this.$store.state.start_string_identifiers.framework_identifier)
				return
			}

			// it's a shortcut if the case_tree_lsdoc_identifier doesn't match the guid pattern
			if (this.case_tree_lsdoc_identifier_is_shortcut) {
				// console.warn('redirect_shortcut: ' + this.case_tree_lsdoc_identifier)
				// first check to see if the shortcut is a category abbreviation
				let cr = this.category_records.find(x=>x.map_label == this.case_tree_lsdoc_identifier)
				if (cr) {
					// if 'bp' was in the url string and bp_review_full_reports_available is true
					if (vapp.bp_requested_in_url && this.bp_review_full_reports_available) {
						// go to the table view (where the bp report data is shown), but only initially
						this.framework_list_view_mode = 'table'
						vapp.bp_requested_in_url = false
					}
					this.category_filter_value = cr.category
					this.handle_search()
					return
				}

				let fr = this.framework_records.find(x=>x.ss_framework_data.shortcuts.find(y=>y==this.case_tree_lsdoc_identifier))
				if (!fr) {
					this.$alert(sr('Unknown shortcut: “$1”', this.case_tree_lsdoc_identifier))
					vapp.go_to_route('')
				} else {
					this.view_framework(fr.lsdoc_identifier)
				}
			
			// else if the current router path points to a framework and this is a registry site, call view_framework to show the dialog for the framework
			} else if (!this.open_mirrors_in_satchel && !empty(this.case_tree_lsdoc_identifier)) {
				this.view_framework(this.case_tree_lsdoc_identifier)
			}
		},

		adoption_status_css_class(doc) {
			return CFDocument.adoption_status_css_class(doc)
		},

		framework_color(doc) {
			const framework_color = U.framework_color(doc.identifier)
			if (!isNaN(framework_color)) return 'k-framework-color-' + framework_color + '-dark'
			return ''
		},
		framework_color_style(doc) {
			return U.framework_color_object(doc.identifier)
		},

		framework_icon(doc) {
			if (U.is_resource_framework(doc)) return 'fas fa-cubes'
			else return 'fas fa-map'
		},

		formatted_date(s) {
			return U.local_last_change_date_time(s)
		},

		create_category_records() {
			// this fn is in case_utilities, because we also need it for FrameworkSwitcher and UserPrefs
			this.category_records = U.create_category_records(
				this.$store.getters.filtered_framework_records,
				this.framework_list_show_sandboxes,
				this.show_mirrors_only
			)
			this.updateSearchStatusMessage()
		},

		category_filter_input(val) {
			this.handle_search()
		},

		handle_search() {
			// establish the debounce fn if necessary
			if (empty(this.handle_search_debounced)) {
				this.handle_search_debounce_worker = (s) => {
					// console.warn('handle_search', this.category_filter_value)
					let search_term_res = U.create_search_re(this.search)

					for (let cr of this.category_records) {
						let cr_showing
						// if a category is selected from the filter, we show only its frameworks, hiding everything else
						if (!empty(this.category_filter_value)) {
							if (cr.category == this.category_filter_value) {
								cr_showing = true
								cr.showing = true
							} else {
								for (let fr of cr.framework_records) {
									cr.doc_showing[fr.lsdoc_identifier] = false
								}
								cr.showing = false
								continue
							}
						} else {
							cr_showing = true
						}

						// go through all framework_records in the category
						for (let fr of cr.framework_records) {
							// if the search is 'mirror', just show all mirrors
							if (s == 'mirror') {	//  && this.framework_list_view_mode == 'table') {
								if (fr.ss_framework_data.is_mirror == 'yes') {
									cr.doc_showing[fr.lsdoc_identifier] = true
									cr_showing = true
								} else {
									cr.doc_showing[fr.lsdoc_identifier] = false
								}

							// if search is empty, show all doc titles
							} else if (empty(s)) {
								cr.doc_showing[fr.lsdoc_identifier] = true
								
							} else {
								// else if title or identifier matches search, show (and make sure cr is showing)
								let search_title = fr.json.CFDocument.title.toLowerCase()
								if (!empty(fr.ss_framework_data.sandboxOfIdentifier)) search_title += ' sandbox'
								search_title += fr.lsdoc_identifier

								if (U.strings_match_search_term_res(search_term_res, [search_title])) {
									cr.doc_showing[fr.lsdoc_identifier] = true
									cr_showing = true
								} else {
									cr.doc_showing[fr.lsdoc_identifier] = false
								}
							}
						}
						cr.showing = cr_showing
					}
					this.updateSearchStatusMessage()
				}
				this.handle_search_debounced = U.debounce(function(s) {
					this.handle_search_debounce_worker(s)
				}, 200)
			}

			// if search is empty, handle it right away
			let s = $.trim(this.search).toLowerCase()
			if (empty(s)) {
				this.handle_search_debounce_worker(s)
			} else {
				// else call the debounce fn
				this.handle_search_debounced(s)
			}
		},

		import_framework() {
			if (this.import_framework_dialog_pending) return
			this.import_framework_dialog_pending = true
			this.remember_new_framework_focus_origin()
			this.new_framework_menu_launching_surface = true
			this.new_framework_menu_open = false
			this.$nextTick(() => {
				window.setTimeout(() => {
					this.new_framework_menu_launching_surface = false
					this.$prompt({
						title: 'Import Framework',
						text: 'Choose a CASE JSON file from your computer as the imported framework source.<div class="mt-2">Note: if a framework with the uploaded file’s identifier (GUID) already exists on the server, it will be overwritten by the uploaded file.</div>',
						promptType: 'file', // default is 'text'
						acceptText: 'Import',
						focusOriginEl: this.new_framework_focus_origin
					})
						.then(file => {
							if (empty(file) || empty(file.name)) return
							// we receive a file from dialog-promise-pwet here; create a FileReader
							let reader = new FileReader()
							reader.onload = e => {
								// NOTE: keep this in synch with re_import_framework from DocumentEditor.vue
								// currently we only support CASE JSON imports

								// parse json (it will be stringified by save_framework_data)
								let json
								try {
									json = JSON.parse(e.target.result)
								} catch (e) {
									console.log(e)
									this.$alert(
										'The uploaded file was not valid JSON (an error occurred when attempting to parse the file).'
									)
									return
								}

								// if no CFDocument, it's not valid json
								// TODO: do more validation...
								if (!json.CFDocument) {
									this.$alert(
										'The uploaded JSON did not include a CFDocument property, so it is not a valid CASE framework file.'
									)
									return
								}

								// see if this file already exists
								let index = this.framework_records.findIndex(x => x.lsdoc_identifier == json.CFDocument.identifier)
								if (index > -1) {
									this.$confirm({
										title: 'Overwrite Existing Framework',
										text: sr(
											'This framework (“$1”) already exists. If you re-import it, the framework’s CASE JSON will be replaced by the newly-imported JSON. (Application-specific data such as the framework’s color will be preserved.) Do you want to proceed?',
											json.CFDocument.title
										),
										acceptText: 'Re-Import CASE JSON',
										dialogMaxWidth: 600
									})
										.then(y => {
											this.complete_import_framework(json)
										})
										.catch(n => {
											console.log(n)
										})
										.finally(f => {})
								} else {
									this.complete_import_framework(json)
								}
							}
							// trigger the FileReader to load the text of the file
							reader.readAsText(file)
						})
						.catch(n => {
							console.log(n)
						})
						.finally(f => {
							this.import_framework_dialog_pending = false
						})
				}, 0)
			})
		},

		complete_import_framework(json) {
			// set lsdoc_identifier in the json, and set update_document_date to 'no' so the server *doesn't* change the lastChangeDateTime
			json.lsdoc_identifier = json.CFDocument.identifier
			json.update_document_date = 'no'
			json.reimport = 'yes' // re-import the entire json file

			// then save using the same save_framework_data fn that we use for creating new frameworks and saving changes to frameworks
			this.$store.dispatch('save_framework_data', json).then(() => {
				// when we come back, add or update the document in the framework_records list
				let cfd_json = new CFDocument(json.CFDocument).to_json() // it's probably overkill to do these transformation, but it shouldn't hurt

				let index = this.framework_records.findIndex(x => x.lsdoc_identifier == cfd_json.identifier)
				if (index == -1) {
					let framework_record = U.create_framework_record(cfd_json.identifier, { CFDocument: cfd_json })
					this.$store.commit('set', [this.framework_records, 'PUSH', framework_record])
				} else {
					// if we're "re-importing" an existing framework, create a new framework_record object, copying in old_framework's ss_framework_data; then when we open it we'll reload the new json from the server
					// note that if this is the case we won't have updated the ss_framework_data record in the DB, so we should get these values back when we reload
					let framework_record = U.create_framework_record(
						cfd_json.identifier,
						{ CFDocument: cfd_json },
						$.extend(true, {}, this.framework_records[index].ss_framework_data)
					)
					this.$store.commit('set', [this.framework_records, 'SPLICE', index, framework_record])
				}

				// call hide_case_tree first, in case this was called from an open framework
				this.hide_case_tree()

				// then re-open the framework (if we don't delay by some measurable amount of time, we sometimes get an error with the stamp reminder)
				setTimeout(x => this.view_framework(json.lsdoc_identifier), 100)
			})
		},

		create_new_framework() {
			this.remember_new_framework_focus_origin()
			// generate a new CFDocument with an identifier, and a sample uri
			let cfd = new CFDocument()
			cfd.generate_identifier()
			cfd.generate_uri(vapp.$store.state.framework_domain)

			// TODO: remember last-entered/accessed creator and match that here
			cfd.creator = this.user_info.first_name + ' ' + this.user_info.last_name

			// TODO: remember last-entered/accessed adoptionStatus and language
			cfd.adoptionStatus = 'Draft'
			cfd.language = 'en'

			// we'll know it's a new framework because ss_framework_data.framework_json_loaded will be false
			let framework_record = U.create_framework_record(cfd.identifier, { CFDocument: cfd.to_json() })

			// new frameworks are private to start
			framework_record.ss_framework_data.is_private = true

			// if the user can only create frameworks for their subdomain, set category (DocumentEditor will lock this)
			if (!this.can_create_new) {
				let re = new RegExp(`\\[\\[${this.user_info.allow_to_create_frameworks_for_category}\\]\\]`)
				let category = vapp.$store.state.framework_categories.find(x=>x.search(re) > 0)
				// the category might not actually exist (if this is the first framework for the subdomain); if so, make the category out of allow_to_create_frameworks_for_category and user_info.organization
				if (empty(category)) {
					// get number for last category in the current list
					let n = '0'
					if (vapp.$store.state.framework_categories[vapp.$store.state.framework_categories.length-1].search(/^(\d+).*/) > -1) {
						n = RegExp.$1
					}
					// get organization from user_info; if it doesn't exist (which shouldn't happen), use the user's email
					let organization = this.user_info.organization ?? this.user_info.email

					// '17 Lincoln Learning Solutions [[LLS]]'
					category = `${n} ${organization} [[${this.user_info.allow_to_create_frameworks_for_category}]]`
				}

				framework_record.ss_framework_data.category = category
				console.warn('category: ' + framework_record.ss_framework_data.category)

				// also set user_rights in this case; this will allow the user to administer the framework right after it's saved (if the user follows through with saving it)
				framework_record.ss_framework_data.user_rights = {}
				framework_record.ss_framework_data.user_rights[this.user_info.user_id] = 'admin'

			// else if we're in a subdomain with a home_category, set category to that home_category (this might be a system admin, who could in theory switch to a different category)
			} else if (this.home_category) {
				let re = new RegExp(`\\[\\[${this.home_category}\\]\\]`)
				framework_record.ss_framework_data.category = vapp.$store.state.framework_categories.find(x=>x.search(re) > 0)
				console.warn('category(2): ' + framework_record.ss_framework_data.category)
			}

			// store framework_record in state.framework_records; we'll take it back out if the user cancels
			this.$store.commit('set', [this.framework_records, 'PUSH', framework_record])

			// set new_framework to open the editor
			this.new_framework = framework_record
		},

		cancel_new_framework() {
			// the DocumentEditor will take care of removing the new_framework from framework_records if necessary
			this.new_framework = null
		},

		import_framework_mirror() {
			this.remember_new_framework_focus_origin()
			this.show_import_mirror_dialog = true
			return
		},
		remember_new_framework_focus_origin() {
			const activator = this.$refs.new_framework_menu_button
			this.new_framework_focus_origin = activator?.$el || activator || null
		},

		delete_frameworks() {
			// console.log(this.selected_frameworks)
			let framework_words = U.ps(
				'the selected framework',
				this.selected_frameworks.length,
				'the ' + this.selected_frameworks.length + ' selected frameworks'
			)
			this.$confirm({
				title: 'Delete Framework(s)',
				text: sr(
					'Are you sure you want to delete $1? The framework(s) will be moved to the archives, and will no longer appear in the framework list.',
					framework_words
				),
				acceptText: 'Delete',
				acceptColor: 'red'
			})
				.then(y => {
					U.loading_start()
					let processed_docs = 0
					for (let doc of this.selected_frameworks) {
						++processed_docs
						this.$store.dispatch('delete_lsdoc', doc.identifier).then(() => {
							--processed_docs
							if (processed_docs == 0) {
								U.loading_stop()
								this.selected_frameworks = []
							}
						})
					}
				})
				.catch(n => {
					console.log(n)
				})
				.finally(f => {})
		},

		open_selected_crosswalk() {
			this.open_crosswalk([this.selected_frameworks[0].identifier, this.selected_frameworks[1].identifier])
		},

		open_crosswalk(identifiers) {
			// make sure crosswalk list is not showing
			this.show_crosswalks_for_identifier = null
			vapp.go_to_route(`crosswalk/${identifiers[0]}/${identifiers[1]}`)
		},

		copy_framework_data() {
			// TODO: no longer prevent this from being exported, but maybe we need to explain about what API URLs you're getting...
			if (vapp.signed_in_only('copy framework data to the clipboard')) return

			let msg = '<div class="mb-2"><b>Please note:</b> data will only be copied for frameworks that are currently showing in the table. You may need to set “Rows per page” to “All” (at the bottom of the table) in order to copy data for all frameworks.</div>'
			msg += `The following data will be copied to your clipboard for each of the ${this.selected_frameworks.length} selected frameworks:<ul>`
			msg += '<li>The framework <b>identifier</b></li>'
			msg += `<li>The ${this.site_config.app_name} <b>category</b> for the framework</li>`
			msg += '<li>The framework <b>title</b></li>'
			msg += '<li>The framework <b>subject</b></li>'
			msg += '<li>The <b>adoption status</b> of the framework</li>'
			msg += '<li>The <b>last-changed date</b> of the framework</li>'
			msg += `<li>The <b>CASE REST API URL</b> for downloading the framework in CASE JSON format <b>from ${this.site_config.app_name}</b></li>`
			if (this.instance_code == 'rosetta_exchange') {
				msg += '<li>The <b>CASE REST API URL</b> for downloading the framework in CASE JSON format <b>from the issuing agency’s CASE server</b> (for mirrored frameworks)</li>'
			}
			msg += `</ul><div class="my-2">Select a format for the ${this.site_config.app_name} CASE REST API URLs to be copied:</div>`
			msg += sr('<ul class="mb-2"><li><b>CASE 1.0</b> includes only fields that are part of the official CASE 1.0 specification.</li><li><b>CASE 1.1</b> includes fields added in CASE 1.1</li><li><b>Extended CASE</b> includes all CASE 1.1 fields, as well as additional fields that are editable/viewable in $1, but are not part of the official CASE spec.</li></ul>', this.$store.state.site_config.app_name)

			this.$prompt({
				title: 'Copy Framework Data to Clipboard',
				text: msg,
				promptType: 'select',
				selectOptions: [{value:'v1p0', text: 'CASE 1.0'}, {value:'v1p1', text: 'CASE 1.1'}, {value:'vext', text: 'Extended CASE'}],
				initialValue: 'v1p1',
				acceptText: 'Copy Data To Clipboard',
				dialogMaxWidth: 820,
			}).then(format => {
				let s = 'identifier	category	title	subject	adoptionStatus	lastChangeDateTime	API-URL'
				if (this.instance_code == 'rosetta_exchange') {
					s += '-1	API-URL-2'
				}
				s += '\n'

				for (let doc of this.selected_frameworks) {
					let fr = this.framework_records.find(x=>x.lsdoc_identifier == doc.identifier)
					s += fr.json.CFDocument.identifier + '\t'
					// Note (PW): for data analysis purposes, additional fields can be added like so when running the app locally
					// s += (fr.json.CFDocument.publisher || '') + '\t'
					s += doc.category + '\t'
					s += fr.json.CFDocument.title + '\t'
					s += doc.subject + '\t'
					s += fr.json.CFDocument.adoptionStatus + '\t'
					s += fr.json.CFDocument.lastChangeDateTime + '\t'

					// always include the API for this server
					s += U.case_api_url(fr.json.CFDocument.identifier, 'CFPackages', format)

					// and for rosetta exchange, also include the mirrored API, unless it's only available via rosetta exchange
					// TODO: not sure if this logic is airtight...
					if (this.instance_code == 'rosetta_exchange' && !fr.json.CFDocument.publisher.includes('Common Good Learning Tools')) {
						let mirror_source = fr.ss_framework_data.mirror_source_rest_api_url || ''
						mirror_source = mirror_source.replace(/vext/, 'v1p1')
						s += '\t' + mirror_source
					}

					s += '\n'
				}
				U.copy_to_clipboard(s)
				this.$alert('Data copied to clipboard. You can now paste this data into any spreadsheet or text editor.')
			})
		},

		export_frameworks() {
			this.$alert(
				'This functionality is coming… In the meantime, you can view individual frameworks and download them from there.'
			)
		},

		rename_frameworks() {
			this.$alert(
				'This functionality is coming… In the meantime, you can view individual frameworks and rename them from there.'
			)
		},

		change_framework_status() {
			this.$prompt({
				title: 'Update Adoption Status for Frameworks',
				text: 'Select the new adoption status for the selected frameworks:',
				promptType: 'select',
				selectOptions: CFDocument.adoption_statuses,
				acceptText: 'Update adoption statuses',
				dialogMaxWidth: 620
			}).then(adoptionStatus => {
				for (let doc of this.selected_frameworks) {
					let data = {
						lsdoc_identifier: doc.identifier,
						// we only have to send in the properties that are changed
						CFDocument: { identifier: doc.identifier, adoptionStatus: adoptionStatus },
						// temporarily get an edit lock, save the change, and then remove the lock
						check_out_and_in: 'yes'
					}
					this.$store.dispatch('save_framework_data', data).then(result => {
						// update adoptionStatus and lastChangeDateTime from result
						data.CFDocument.lastChangeDateTime = result.document_lastChangeDateTime

						// update the framework_record
						let fr = this.framework_records.find(x => x.lsdoc_identifier == data.lsdoc_identifier)
						this.$store.commit('set', [fr.json.CFDocument, 'adoptionStatus', data.CFDocument.adoptionStatus])
						this.$store.commit('set', [fr.json.CFDocument, 'lastChangeDateTime', result.document_lastChangeDateTime])

						// also update to the document's cftree, if we have one
						if (!empty(fr.cfo?.cftree)) {
							this.$store.commit('set', [fr.cfo.cftree, 'lastChangeDateTime', result.document_lastChangeDateTime])
							this.$store.commit('set', [fr.cfo.cftree, 'adoptionStatus', data.CFDocument.adoptionStatus])
							this.$store.commit('set', [
								fr.cfo.cftree.cfitem,
								'lastChangeDateTime',
								result.document_lastChangeDateTime
							])
							this.$store.commit('set', [fr.cfo.cftree.cfitem, 'adoptionStatus', data.CFDocument.adoptionStatus])
						}
					})
				}
			})
		},

		get_query(mode, val) {
			let query = {}

			if (mode == 'minimized') {
				if (val) query.minimized = null
			} else {
				if (this.minimized_param) query.minimized = null
			}

			return query
		},

		get_query_string(mode, val) {
			let query = this.get_query(mode, val)
			let s = ''
			for (let key in query) {
				if (s) s += '&'
				s += key
			}
			if (s) s = '?' + s
			return s
		},

		hide_case_tree() {
			this.$router.push({ name: 'framework_list', query: this.get_query() })
			vapp.pm_send('hide_tree')

			// re-call create_category_records, in case a framework's category was changed
			this.create_category_records()
		},

		view_framework(doc_identifier, item_identifier, $event) {
			// if doc_identifier is an array, extract the three params from the array
			if ($.isArray(doc_identifier)) {
				[doc_identifier, item_identifier, $event] = doc_identifier
			}

			// if we're not showing mirrors here, it's a "registry" site, so just show the API url, plus some other guidance
			if (!this.open_mirrors_in_satchel) {
				let fr = this.framework_records.find(x=>x.lsdoc_identifier == doc_identifier)
				let mirror_source = fr?.ss_framework_data?.mirror_source_rest_api_url

				if (!mirror_source) {
					// this shouldn't currently happen, because CN2/Rosetta should only includes mirrors.
					// for now we'll just assume that if we get this we need to redirect to rosetta; later we could change this if we need to support registry servers that are a mixture of mirrors and non-mirrors
					mirror_source = `${this.site_config.case_portal_url}/ims/case/v1p0/CFPackages/${doc_identifier}`
				}

				// if mirror_source uses vext, sub v1p1
				mirror_source = mirror_source.replace(/vext/, 'v1p1')

				// also, sub rosetta for satchelcommons urls that don't have a subdomain
				mirror_source = mirror_source.replace(/https:\/\/satchelcommons.com/, 'https://rosetta.commongoodlt.com')

				let msg = `<div class="mb-3">`

				// if we can get the category for this framework, add some more info
				let category = this.category_records.find(x=>x.category == fr?.ss_framework_data.category)
				// console.warn('category: ', category)
				if (category) {
					// this will show links to the agency case server and to rosetta
					msg += category.view_framework_msg.replace(/xx_identifier_xx/g, doc_identifier)
				}

				msg += `<div class="mt-6">Click below to copy the <nobr>CASE<sup>®</sup> API url</nobr> for this framework:</div>`
				msg += `<div class="d-flex justify-center mt-3">`
				msg += `<div class="text-center pa-1 amber lighten-5" style="font-size:14px; border:1px solid #666!important; border-radius:4px;"><nobr>${mirror_source}</nobr></div>`
				msg += '</div>'
				
				// for now we assume all cglt-published frameworks are restricted, and other frameworks aren't
				if (category?.cglt_published) {
					msg += `<div class="mt-3 text-center"><i>Note that the publisher of this framework requires OAuth authentication to access framework data via the CASE API.</i></div>`
				}

				msg += `</div>`

				this.$confirm({
					title: fr.json.CFDocument.title,
					text: msg,
					promptType: 'text',
					acceptText: 'Copy CASE API URL',
					acceptIcon: 'fas fa-clipboard',
					dialogMaxWidth: 840
				}).then(y => {
					U.copy_to_clipboard(mirror_source)
					this.$inform('API URL copied to clipboard')
				}).catch(n => {
					console.log(n)
				}).finally(f => {})

				return
			}

			// start url with the document identifier
			let url = '/' + doc_identifier

			// if an item_identifier is specified, add it so we open right to that item
			if (item_identifier) {
				url += '/' + item_identifier

				// else add the doc_identifier again, so that we'll open with the document's tile showing -- but not if we're on a small screen, or if start_string is specified
			} else if (!this.$vuetify.breakpoint.xs && empty(this.$store.state.start_string)) {
				url += '/' + doc_identifier
				// on a small screen, the document's tile will take up too much space, and CASEFrameworkViewer will take care of showing the start_string item
			}

			// add the query string
			url += this.get_query_string()

			// if user holds meta/ctrl key down, open in a new tab/window
			if (U.meta_or_alt_key($event)) {
				window.open(url)
			} else {
				this.$router.push({ path: url })
			}
		},

		show_best_practices(framework_identifier) {
			if (vapp.signed_in_only('show the Best Practices report for this framework', framework_identifier)) return

			// if getters.bp_review_full_reports_available is false, the user can't actually view the full report -- it's probably an edge-case situation where the instance has bp_summary_policy 'always' and bp_full_report_policy 'default' or 'never'
			if (!this.bp_review_full_reports_available) return

			this.best_practices_fr = this.framework_records.find(x=>x.lsdoc_identifier == framework_identifier)
		},

		set_mode(mode, val) {
			// when setting the mode, use replace so that we don't record a new history item
			this.$router.replace({ query: this.get_query(mode, val) })
		},

		set_starting_lsitem_identifier(item_identifier, tree_key) {
			let url

			// tree_key is optional
			if (!empty(tree_key))
				url = sr('/$1/$2/$3$4', this.case_tree_lsdoc_identifier, item_identifier, tree_key, this.get_query_string())
			else url = sr('/$1/$2$3', this.case_tree_lsdoc_identifier, item_identifier, this.get_query_string())

			// $router.push records a new history item, so that the back btn goes back to the previous item; replace doesn't
			this.$router.push({ path: url })
			// this.$router.replace({ path: url })
		},

		site_headline_btn_clicked(params) {
			if (params.click_fn) this[params.click_fn]()
			else if (params.url) window.open(params.url, params.url.replace(/\W/g, ''))
		},
		
		// registration buttons for Rosetta Exchange and Satchel Commons
		start_exchange_register() { vapp.show_exchange_register = true },
		start_commons_register() { vapp.show_commons_register = true },

		dismiss_header() {
			this.framework_list_header_dismissed = true
		},

		bp_severity_indicator(severity_level) {
			let sd = {									// severity data
				0: {
					class: 'green black--text',
					icon: 'fa-badge-check',
				},
				1: {
					class: 'yellow darken-1 black--text',
					icon: 'fa-gauge-simple-min',
				},
				2: {
					class: 'orange black--text',
					icon: 'fa-gauge-simple',
				},
				3: {
					class: 'red lighten-1 black--text',
					icon: 'fa-gauge-simple-max',
				},
			}[severity_level]

			let s = ''

			s += `<b class="${sd.class} k-bp-severity-indicator">`
			s += `<i class="v-icon fas ${sd.icon}"></i>`
			// if (severity_level > 0) s += `<span class="ml-1">${severity_level}</span>`
			s += `</b>`

			return s
		},
	}
}
</script>

<style lang="scss">
.k-framework-list-agency-title {
  position: absolute;
  z-index: 5;
  left: 0px;
  padding: 0 18px;
  top: -2px;
  border-radius: 0 0 8px 0;
  // background-color will be added by k-banner-color
  // color will be added by k-agency-color
  // css can be tweaked by k-agency-title-extra
  font-size: 18px;
  line-height: 30px;
  font-weight: bold;
  cursor: pointer;
}

.k-toolbar-app-title--framework-list {
  display: none;
  padding-left: 0px !important;
  width: 100%;
  text-align: center;
  position: static;
  padding-top: 8px;
  color: #000;
}

.k-framework-list-header-line {
  padding-top: 44px;
  padding-bottom: 4px;
  padding-left: 8px;
  font-size: 26px;
  position:relative;
	z-index:1;
}

.k-framework-list-search-wrapper {
	// max-width:520px;
	display:flex;
	margin:-24px 0px 12px auto;
	position:relative;
	z-index:0;
}

.k-framework-list-wrapper {
  border: 1px solid #ccc;
  border-radius: 10px;
  background-color: #fff;
  padding: 0 6px;
}

.k-framework-list-wrapper-inner {
  max-height: calc(100vh - 192px);
  overflow: auto; // we need this to be overflow:auto to make the tile positioning work right in the case viewer
}

.k-framework-list-table {
  th {
    white-space: nowrap;
  }
  td {
    font-size: 14px !important;
    line-height: 18px;
  }
  a {
    color: #000 !important;
    text-decoration: none !important;
  }
  a:hover {
    text-decoration: none !important;
  }
}

.k-framework-list-headline {
  max-width: 700px;
  margin: 16px auto 20px auto;
  background-color: $v-amber-accent-1;
  color: #000;
  padding: 12px;
  border-radius: 12px;
  font-size: 16px;
  line-height: 22px;

  a {
    font-weight: bold;
    background-color: $v-amber-darken-4 !important;
    color: #fff !important;
    text-decoration: none;
    padding: 0 3px;
    border-radius: 4px;
    white-space: nowrap;
  }
  a:hover {
    text-decoration: underline;
  }
}

.k-framework-list-headline-patch-name {
	font-size:1.2em;
	font-weight:900;
	text-align:center;
	margin-bottom:12px;
	margin-top:8px;
}

.k-framework-list-welcome-msg {
  font-size: 22px;
}

.k-framework-list-link-btn,
.k-framework-list-table-link-btn {
	background: transparent;
	border: 0;
	color: inherit;
	cursor: pointer;
	padding: 0;
	text-align: left;
}

.k-framework-list-table-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	border-radius: 999px;
	color: #fff;
	flex: 0 0 auto;

	.v-icon {
		color: #fff !important;
	}
}

.k-framework-list-table-link-btn:hover,
.k-framework-list-table-link-btn:focus,
.k-framework-list-link-btn:hover,
.k-framework-list-link-btn:focus {
	text-decoration: underline;
}

.k-framework-list-bp-score {
	white-space:nowrap;
	cursor:pointer;
	background: transparent;
	border: 0;
	padding: 0;
	text-align: left;
}

// note that this is also used in BestPracticesReport
.k-bp-severity-indicator {
	padding:2px 8px;
	border-radius:3px;
	font-size:16px!important;
	line-height:16px!important;
	.fas {
		font-size:16px!important;
		line-height:16px!important;
		margin-top:-3px;
	}
}	

.k-framework-selected-category-description {
	max-width:780px;
	margin:12px auto 12px auto;
	background-color:#f2f2f2;
	background-color:$v-amber-lighten-4;
	border:4px solid $v-amber-darken-3;
	border-top-width:20px;
	border-radius:8px;
	padding:12px;
	font-size:15px;
	line-height:20px;
}

.k-framework-list-identifier-text {
	font-size: 12px;
	line-height: 16px;
}

.v-input__icon--clear:focus,
.v-input__icon--clear:focus-within,
.v-input__icon--clear > *:focus {
        opacity: 1 !important;
        visibility: visible !important;
}
</style>
